const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/**
 * Formats a person's name for display as "Last Names, First Names".
 *
 * Handles four input formats:
 *  - Email address (e.g. "juan.ruiz@company.com")  → "Ruiz, Juan"
 *  - Comma-separated "First Names, Last Names"      → "Last Names, First Names"
 *  - Space-separated "First Middle Last"            → "Last, First Middle"
 *  - Single word                                    → capitalized as-is
 */
export function formatDisplayName(name: string): string {
  if (!name?.trim()) return '';

  // Email: derive from local part (juan.manuel.ruiz@company.com → "Ruiz, Juan")
  if (name.includes('@')) {
    const localPart = name.split('@')[0] ?? '';
    const segments = localPart.split('.').filter(Boolean);
    if (segments.length < 2) return localPart;
    return `${capitalize(segments.at(-1) ?? '')}, ${capitalize(segments[0])}`;
  }

  // Comma format: "First Names, Last Names" → "Last Names, First Names"
  if (name.includes(',')) {
    const [firstNames, ...rest] = name.split(',').map((p) => p.trim());
    const lastNames = rest.join(' ').trim();
    if (firstNames && lastNames) {
      return `${lastNames}, ${firstNames}`;
    }
    return name.trim();
  }

  // Space-separated: last word is last name, everything before is given names
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return name;
  if (parts.length === 1) return capitalize(parts[0]);
  const lastName = capitalize(parts.at(-1) ?? '');
  const firstNames = parts.slice(0, -1).map(capitalize).join(' ');
  return `${lastName}, ${firstNames}`;
}
